import type { JsonRecord, RetailerApiClient } from './api-client.ts';

export class RetailerApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly requestId: string | null,
  ) {
    super(message);
    this.name = 'RetailerApiError';
  }
}
export interface RetailerHttpClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /**
   * Browser shells pause a rejected protected request while the user completes
   * MFA, then the original request is retried exactly once.
   */
  onMfaRequired?: () => Promise<void>;
}
function endpoint(baseUrl: string, path: string) {
  return `${baseUrl.replace(/\/$/, '')}${path}`;
}
function queryString(values: Record<string, string | boolean | null | undefined>) {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(values))
    if (value !== null && value !== undefined && value !== '') q.set(key, String(value));
  const result = q.toString();
  return result === '' ? '' : `?${result}`;
}
function idempotencyKey() {
  return globalThis.crypto?.randomUUID?.() ?? `retailer-${Date.now()}-${Math.random()}`;
}
function asObject(value: unknown): JsonRecord {
  return value !== null && typeof value === 'object' ? (value as JsonRecord) : {};
}

export function createRetailerHttpClient(
  options: RetailerHttpClientOptions = {},
): RetailerApiClient {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const baseUrl = options.baseUrl ?? env?.VITE_RETAILER_API_BASE_URL ?? '';
  const fetchImpl = options.fetchImpl ?? fetch;
  async function request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    responseType: 'json' | 'text' = 'json',
    retryAfterMfa = true,
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: responseType === 'text' ? 'text/csv' : 'application/json',
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') headers['Idempotency-Key'] = idempotencyKey();
    const response = await fetchImpl(endpoint(baseUrl, path), {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload: unknown =
      response.status === 204
        ? null
        : responseType === 'text'
          ? await response.text()
          : await response.json().catch(() => null);
    if (!response.ok) {
      const envelope = asObject(payload);
      const error = asObject(envelope.error);
      const apiError = new RetailerApiError(
        typeof error.code === 'string' ? error.code : 'HTTP_ERROR',
        typeof error.message === 'string' ? error.message : 'The request could not be completed.',
        typeof envelope.requestId === 'string' ? envelope.requestId : null,
      );
      if (
        apiError.code === 'PRECONDITION_FAILED' &&
        retryAfterMfa &&
        !path.startsWith('/api/v1/auth/mfa') &&
        options.onMfaRequired !== undefined
      ) {
        await options.onMfaRequired();
        return request(method, path, body, responseType, false);
      }
      throw apiError;
    }
    return payload as T;
  }
  const enc = encodeURIComponent;
  return {
    auth: {
      async signIn(input) {
        const result = await request<JsonRecord>('POST', '/api/v1/auth/session', input);
        return typeof result.expiresAt === 'string'
          ? { ok: true, retailerId: '' }
          : { ok: false, message: 'The sign-in response did not establish a session.' };
      },
      bootstrap: () => request('GET', '/api/v1/retailer/bootstrap'),
      async publicFlags() {
        const payload = await request<{ flags?: unknown }>('GET', '/api/v1/retailer/public-flags');
        return {
          'retailer.lead_capture': asObject(payload.flags)['retailer.lead_capture'] === true,
        };
      },
    },
    mfa: {
      status: () => request('GET', '/api/v1/auth/mfa'),
      beginTotp: () => request('POST', '/api/v1/auth/mfa/totp/begin'),
      confirmTotp: (code) => request('POST', '/api/v1/auth/mfa/totp/confirm', { code }),
      stepUp: (code) => request('POST', '/api/v1/auth/mfa/step-up', { code }),
    },
    leads: { submitRetailer: (input) => request('POST', '/api/v1/retailer/leads', input) },
    dashboard: { health: () => request('GET', '/api/v1/retailer/dashboard') },
    orders: {
      async list() {
        const payload = await request<{ fulfilments?: unknown }>(
          'GET',
          '/api/v1/retailer/fulfilments',
        );
        return (payload.fulfilments ?? []) as never;
      },
      detail: (id) => request('GET', `/api/v1/retailer/fulfilments/${enc(id)}`),
      transition: (id, status, note) =>
        request('POST', `/api/v1/retailer/fulfilments/${enc(id)}/status`, {
          status,
          ...(note === undefined || note === null ? {} : { note }),
        }),
    },
    catalog: {
      async list(input) {
        const payload = await request<{ listings?: unknown }>(
          'GET',
          `/api/v1/retailer/products${queryString(input)}`,
        );
        return (payload.listings ?? []) as never;
      },
      create: (input) => request('POST', '/api/v1/retailer/products', input),
      update: (id, input) => request('PATCH', `/api/v1/retailer/products/${enc(id)}`, input),
      archive: (id) => request('POST', `/api/v1/retailer/products/${enc(id)}/archive`),
      restore: (id) => request('POST', `/api/v1/retailer/products/${enc(id)}/restore`),
      attachMedia: (id, input) =>
        request('POST', `/api/v1/retailer/products/${enc(id)}/media`, input),
      removeMedia: (id, mediaId) =>
        request('DELETE', `/api/v1/retailer/products/${enc(id)}/media/${enc(mediaId)}`),
    },
    inventory: {
      async health(input) {
        const payload = await request<{ balances?: unknown }>(
          'GET',
          `/api/v1/retailer/inventory${queryString(input as Record<string, string | boolean | null | undefined>)}`,
        );
        return (payload.balances ?? []) as never;
      },
      setOnHand: (id, input) => request('PATCH', `/api/v1/retailer/inventory/${enc(id)}`, input),
      setThresholds: (id, input) =>
        request('PATCH', `/api/v1/retailer/inventory/${enc(id)}`, input),
      async movements(id) {
        const payload = await request<{ movements?: unknown }>(
          'GET',
          `/api/v1/retailer/inventory/${enc(id)}/movements`,
        );
        return (payload.movements ?? []) as never;
      },
    },
    imports: {
      async list() {
        return request('GET', '/api/v1/retailer/imports');
      },
      get: (id) => request('GET', `/api/v1/retailer/imports/${enc(id)}`),
      upload: (input) => request('POST', '/api/v1/retailer/imports', input),
      map: (id, input) => request('POST', `/api/v1/retailer/imports/${enc(id)}/mapping`, input),
      validate: (id) => request('POST', `/api/v1/retailer/imports/${enc(id)}/validate`),
      async rows(id) {
        return request('GET', `/api/v1/retailer/imports/${enc(id)}/rows`);
      },
      fixRow: (id, rowId, input) =>
        request('PATCH', `/api/v1/retailer/imports/${enc(id)}/rows/${enc(rowId)}`, input),
      preview: (id) => request('GET', `/api/v1/retailer/imports/${enc(id)}/preview`),
      publish: (id) => request('POST', `/api/v1/retailer/imports/${enc(id)}/publish`),
      report: (id) =>
        request('GET', `/api/v1/retailer/imports/${enc(id)}/report`, undefined, 'text'),
      cancel: (id) => request('POST', `/api/v1/retailer/imports/${enc(id)}/cancel`),
      async templates() {
        return request('GET', '/api/v1/retailer/imports/templates');
      },
      saveTemplate: (input) => request('POST', '/api/v1/retailer/imports/templates', input),
      updateTemplate: (id, input) =>
        request('PATCH', `/api/v1/retailer/imports/templates/${enc(id)}`, input),
    },
    store: {
      read: () => request('GET', '/api/v1/retailer/store-profile'),
      updateLocation: (id, input) =>
        request('PATCH', `/api/v1/retailer/locations/${enc(id)}`, input),
      saveHours: (input) => request('PATCH', '/api/v1/retailer/settings/hours', input),
      pauseHours: (input) => request('PATCH', '/api/v1/retailer/settings/hours/pause', input),
      addHoliday: (input) => request('POST', '/api/v1/retailer/settings/holiday-overrides', input),
      addClosure: (input) => request('POST', '/api/v1/retailer/settings/closures', input),
    },
    afterHours: {
      read: () => request('GET', '/api/v1/retailer/settings/extended-hours'),
      request: (input) =>
        request('POST', '/api/v1/retailer/settings/extended-hours/request', input),
      pause: (input) => request('PATCH', '/api/v1/retailer/settings/extended-hours/pause', input),
    },
    payouts: {
      async overview() {
        const payload = await request<{ payouts?: unknown }>('GET', '/api/v1/retailer/payouts');
        return { payouts: (payload.payouts ?? []) as JsonRecord[] };
      },
      detail: (id) => request('GET', `/api/v1/retailer/payouts/${enc(id)}`),
    },
  };
}
